import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useRouter } from 'next/router'
import { useContext } from 'react'
import { useAsyncFn } from 'react-use'
import { ContextShell } from '../../../modules/context'

interface UseDeletePlaylist {
  playlistDeleted: boolean
  error?: boolean
  loading: boolean
  deletePlaylist(i: {
    playlistId: string
    events?: {
      [key: string]: string
    }
  }): void
}

export const useDeletePlaylist = (): UseDeletePlaylist => {
  const { i18n } = useLingui()
  const { pathname, push } = useRouter()
  const {
    MoisesCLI,
    toast: { add: addToast },
    event: { sendEvent }
  } = useContext(ContextShell)

  const isDevEnvironment = process.env.NEXT_PUBLIC_LOCAL_DEV

  const [state, deletePlaylist] = useAsyncFn(
    async ({
      playlistId,
      events = {}
    }: {
      playlistId: string
      events?: {
        [key: string]: string
      }
    }) => {
      let result

      try {
        result = await MoisesCLI.deletePlaylist({ playlistId })

        addToast({
          icon: null,
          closable: true,
          type: 'success',
          description: i18n._(t`setlist_delete_success`)
        })

        sendEvent({
          name: 'playlist_removed',
          category: 'retention',
          customAttributes: {
            playlist_id: playlistId,
            timestamp: new Date().toISOString(),
            ...events,
            reason: 'deleted'
          }
        })

        if (pathname !== '/library') {
          push(`${isDevEnvironment ? '/studio' : ''}/library`)
        }
      } catch (e: any) {
        addToast({
          icon: null,
          type: 'error',
          closable: true,
          title: i18n._(t`error.deleting.playlist.title`),
          description: i18n
            ._(t`error.create.playlist.description`)
            .replace(/\*/g, '')
        })
      }

      return result
    },
    [i18n, MoisesCLI, addToast, pathname, push, sendEvent]
  )

  const { loading, error, value } = state

  return {
    ...state,
    loading,
    error: !!error,
    playlistDeleted: value === true,
    deletePlaylist
  }
}
